import type { PostHog } from 'posthog-js';
import type { ProductAnalyticsConfig } from 'lib.store/src/main/product-analytics/models';
import type { AnalyticsProperties } from './types';

/**
 * The provider adapter. Everything above this file speaks in events and
 * properties; only this file knows which analytics vendor is behind them, so
 * swapping or adding one never reaches a surface.
 *
 * The vendor bundle is imported dynamically: a public sponsor deck should not
 * pay ~60 KB up front for telemetry, and the environments where analytics is
 * switched off must not download it at all. Calls made before the bundle
 * lands are queued rather than dropped — the first events of a session (the
 * page open) are the ones a funnel cannot do without.
 */

let client: PostHog | null = null;
let state: 'idle' | 'loading' | 'ready' | 'disabled' = 'idle';

/**
 * Whether session replay may run at all: enabled in config AND inside this
 * browser's sample. Recording is never started globally — a measured surface
 * (see AnalyticsSurface) or the signed-in product (a sponsor's or an
 * organizer's account, for the admin's per-user session list) asks for it.
 */
let replayAllowed = false;

/**
 * How many mounted owners want recording, independent of whether the client
 * exists yet. A count, not a flag: the start page's surface unmounting must not
 * stop the recording the signed-in product asked for, nor the reverse.
 */
let replayRequests = 0;

const queue: Array<(instance: PostHog) => void> = [];

/**
 * Whether this browser falls inside the replay sample.
 *
 * Derived from the visitor id rather than drawn at random, so the answer is the
 * same on every page load. The journey through these surfaces includes a full
 * page hand-off (registration into the platform), and a fresh draw
 * there would record one half of a funnel and not the other — half a replay
 * answers nothing.
 */
const isSampled = (config: ProductAnalyticsConfig): boolean => {
  if (config.session_replay_sample_rate >= 1) {
    return true;
  }

  if (config.session_replay_sample_rate <= 0 || !config.distinct_id) {
    return false;
  }

  let hash = 0;
  for (let index = 0; index < config.distinct_id.length; index += 1) {
    hash = (hash * 31 + config.distinct_id.charCodeAt(index)) % 100_000;
  }

  return hash / 100_000 < config.session_replay_sample_rate;
};

/**
 * Runs an operation against the client, or queues it until the bundle is in.
 */
const withClient = (operation: (instance: PostHog) => void): void => {
  if (state === 'disabled') {
    return;
  }

  if (client) {
    operation(client);

    return;
  }

  queue.push(operation);
};

/**
 * Boots the provider from the backend-served configuration. Idempotent: the
 * config query is cached and its effect may re-run.
 */
export const initAnalytics = async (config: ProductAnalyticsConfig): Promise<void> => {
  if (state !== 'idle') {
    return;
  }

  if (!config.enabled || !config.api_key || !config.api_host) {
    state = 'disabled';
    queue.length = 0;

    return;
  }

  state = 'loading';

  let posthog: PostHog;
  try {
    posthog = (await import('posthog-js')).default;
  } catch {
    // a blocked or failed vendor bundle must never break the page it measures
    state = 'disabled';
    queue.length = 0;

    return;
  }

  posthog.init(config.api_key, {
    api_host: config.api_host,
    // the same cross-domain visitor id the event landings report under, so one
    // prospect is one person. Bootstrapped rather than identify()'d after init,
    // which would attribute the page open to a throwaway anonymous person first;
    // marked identified because that is the state the landings leave this id in
    ...(config.distinct_id
      ? { bootstrap: { distinctID: config.distinct_id, isIdentifiedID: true } }
      : {}),
    debug: config.debug,
    // scales with the whole product's traffic, not just the measured surfaces
    autocapture: config.autocapture,
    // heatmaps need autocapture's raw clicks, so they follow it
    enable_heatmaps: config.autocapture,
    capture_pageview: true,
    capture_pageleave: true,
    // never recorded by default: a measured surface starts it (see below)
    disable_session_recording: true,
    session_recording: {
      // every input, not just credentials. The measured surfaces ask sponsors to
      // type their own words — questions to the agent, why a creative was
      // rejected — and the events deliberately report only the shape of that
      // text. Replay must not quietly reintroduce the content it excludes; no
      // metric here needs what was typed, only that the field was used
      maskAllInputs: true,
    },
    persistence: 'localStorage+cookie',
    loaded: (instance) => {
      instance.register({ environment: config.environment });
    },
  });

  client = posthog;
  state = 'ready';

  // decided BEFORE the queue is flushed: a surface that mounted during the cold
  // start has its replay request waiting in there, and it must see the verdict
  replayAllowed = config.session_replay && isSampled(config);

  queue.splice(0).forEach((operation) => operation(posthog));
};

/**
 * Records one event.
 */
export const captureAnalytics = (event: string, properties: AnalyticsProperties = {}): void => {
  withClient((instance) => instance.capture(event, properties));
};

/**
 * Attaches properties to every subsequent event of this browser.
 */
export const registerAnalytics = (properties: AnalyticsProperties): void => {
  withClient((instance) => instance.register(properties));
};

/**
 * Records who the current person turned out to be, without changing WHICH
 * person they are. Identity stays the platform's visitor id for the whole
 * journey — see useIdentifyUser for why switching it would break the funnel.
 */
export const setPersonProperties = (properties: AnalyticsProperties): void => {
  withClient((instance) => instance.setPersonProperties(properties));
};

/**
 * Associates the person with a group (organization, event) so metrics can be
 * aggregated per account rather than per browser.
 */
export const groupAnalytics = (
  type: string,
  key: string,
  properties: AnalyticsProperties = {},
): void => {
  withClient((instance) => instance.group(type, key, properties));
};

/**
 * Brings recording in line with what the surfaces want. Queued like any other
 * operation, so a request made during the cold start is honoured once the
 * client exists rather than dropped — on these surfaces the cold visit IS the
 * traffic. Sampling is per browser, not per surface: half a journey replays
 * nothing useful.
 */
const applyReplay = (instance: PostHog): void => {
  if (!replayAllowed) {
    return;
  }

  if (replayRequests > 0) {
    instance.startSessionRecording();

    return;
  }

  instance.stopSessionRecording();
};

/**
 * Asks for session replay — a surface being studied, or the signed-in
 * product. Every call is paired with one stopSessionReplay().
 */
export const startSessionReplay = (): void => {
  replayRequests += 1;
  withClient(applyReplay);
};

/**
 * Withdraws one replay request when its owner goes away; recording stops once
 * no owner wants it, so it never leaks past what asked for it. Also settles the
 * case where an owner unmounts before the client finished loading: the queued
 * call reads the latest count, not the one at the time it was queued.
 */
export const stopSessionReplay = (): void => {
  replayRequests = Math.max(0, replayRequests - 1);
  withClient(applyReplay);
};

// There is deliberately no reset(): identity is a server-issued HttpOnly cookie,
// so clearing the client's copy would only make the next boot fetch the same id
// back from /api/analytics/config. Forgetting a person here means rotating that
// cookie, which would cut the landing → deck → sign-up join for everyone.
